
import { SUBSCRIPTION_PLANS } from '../config/subscriptionPlans';
import * as companyApi from './api/companyApi';
import { API_CONFIG } from '../config/apiConfig';

// Simulated delay for API calls
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class CompanyService {
    constructor() {
        this.companies = [];
    }

    // Get all companies (paginated)
    async getCompanies(params = {}) {
        console.log('[CompanyService] Fetching companies from backend API...', params);
        // Call the real backend API - NO MOCK FALLBACK
        const response = await companyApi.getAllTenants(params);

        // Backend returns paginated response - extract from content array
        const tenants = response.content || response;
        const tenantsList = Array.isArray(tenants) ? tenants : [];

        console.log('[CompanyService] Successfully fetched tenants:', tenantsList.length);

        // Map backend TenantResponseDTO to frontend company structure
        const companies = tenantsList.map(tenant => ({
            id: tenant.tenantId,
            name: tenant.businessName,
            email: tenant.email,
            supplierId: tenant.supplierId,
            subscriptionPlan: tenant.subscriptionPlan?.toLowerCase() || 'basic',
            status: tenant.status?.toLowerCase() || 'active',
            isolationStrategy: tenant.isolationStrategy,
            schemaName: tenant.schemaName,
            subdomain: tenant.subdomain || '',
            maxUsers: tenant.maxUsers || 0,
            maxOrders: tenant.maxOrders || 0,
            maxProducts: tenant.maxProducts || 0,
            // Fields not available from backend - set defaults
            tradeLicenseNo: tenant.tradeLicenseNo || 'N/A',
            businessType: tenant.businessType || 'N/A',
            category: tenant.categoryName || 'N/A',
            description: tenant.description || '',
            contactPerson: tenant.contactName || 'N/A',
            phone: tenant.phone || 'N/A',
            whatsapp: tenant.whatsapp || 'N/A',
            addressLine: tenant.addressLine || tenant.address || 'N/A',
            city: tenant.city || 'N/A',
            country: 'UAE',
            subscriptionStartDate: tenant.subscriptionStartDate,
            subscriptionExpiryDate: tenant.subscriptionEndDate,
            logo: null,
            registeredDate: null,
            userCount: 0,
            storageUsed: 0
        }));

        // Return paginated structure if available, else just array
        if (response.content !== undefined) {
            return {
                ...response,
                content: companies,
                data: companies // For DataTable compatibility if it expects data
            };
        }
        
        return companies;
    }

    // Get company by ID
    // Get company by ID
    async getCompanyById(id) {
        // Since backend doesn't have get-by-id yet, we fetch all and find
        const result = await this.getCompanies();
        const companiesList = Array.isArray(result) ? result : (result.content || []);
        const company = companiesList.find(c => c.id === id);

        if (!company) {
            throw new Error(`Company with ID ${id} not found`);
        }

        return company;
    }

    // Create new company
    async createCompany(companyData) {
        try {
            console.log('[CompanyService] Registering new company via backend API:', companyData.name);

            // Extract owner name from contactPerson
            const nameParts = (companyData.contactPerson || '').trim().split(' ');
            const ownerFirstName = nameParts[0] || 'Owner';
            const ownerLastName = nameParts.slice(1).join(' ') || 'User';

            // Generate username from email
            const ownerUsername = (companyData.email || '').split('@')[0] || 'user' + Date.now();

            // Generate temporary password
            const ownerPassword = 'TempPassword@123';

            // Map frontend data to backend RegisterTenantCommand
            const registrationData = {
                // Company Details
                companyName: companyData.name,
                companyEmail: companyData.email,
                companyPhone: companyData.phone,
                companyAddress: `${companyData.addressLine || ''}, ${companyData.city || ''}, ${companyData.country || 'UAE'}`.trim(),
                taxId: companyData.tradeLicenseNo,
                subdomain: companyData.subdomain || '',

                // Owner Details
                ownerFirstName,
                ownerLastName,
                ownerEmail: companyData.email,
                ownerUsername,
                ownerPassword,
                ownerPhone: companyData.phone,

                // Subscription
                subscriptionPlan: (companyData.subscriptionPlan || 'basic').toUpperCase(),

                // WhatsApp Integration (optional)
                whatsappPhoneNumberId: companyData.whatsapp ? companyData.whatsapp.replace(/[^0-9]/g, '') : null,

                // Additional metadata
                metadata: JSON.stringify({
                    businessType: companyData.businessType,
                    category: companyData.category,
                    description: companyData.description,
                    subscriptionStartDate: companyData.subscriptionStartDate,
                    subscriptionExpiryDate: companyData.subscriptionExpiryDate,
                    status: companyData.status
                })
            };

            // Call backend registration API
            const response = await companyApi.registerTenant(registrationData);

            console.log('[CompanyService] Registration successful:', response);

            // Return the registration result with credentials
            return {
                success: response.success,
                tenantId: response.tenantId,
                supplierId: response.supplierPublicId,
                keycloakUserId: response.keycloakUserId,
                credentials: {
                    username: ownerUsername,
                    password: ownerPassword,
                    email: companyData.email
                },
                message: response.message || 'Company registered successfully'
            };
        } catch (error) {
            console.error('[CompanyService] Registration failed:', error);
            throw new Error(error.message || 'Failed to register company. Please try again.');
        }
    }

    // Update company
    async updateCompany(id, companyData) {
        try {
            console.log('[CompanyService] Updating company via API:', id, companyData);
            const response = await companyApi.updateCompany(id, companyData);
            return response;
        } catch (error) {
            console.error('[CompanyService] Update failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to update company. Please try again.');
        }
    }

    // Delete company
    async deleteCompany(id) {
        await delay(300);
        const index = this.companies.findIndex(c => c.id === id);
        if (index === -1) {
            throw new Error(`Company with ID ${id} not found`);
        }
        this.companies.splice(index, 1);
        return { success: true, message: 'Company deleted successfully' };
    }

    // Get subscription plans
    async getSubscriptionPlans() {
        await delay(200);
        return [...SUBSCRIPTION_PLANS];
    }

    // Get plan by ID
    async getPlanById(planId) {
        await delay(100);
        const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
        if (!plan) {
            throw new Error(`Plan with ID ${planId} not found`);
        }
        return { ...plan };
    }

    // Search companies
    async searchCompanies(query) {
        await delay(300);
        const lowerQuery = query.toLowerCase();
        return this.companies.filter(company =>
            company.name.toLowerCase().includes(lowerQuery) ||
            company.tradeLicenseNo.toLowerCase().includes(lowerQuery) ||
            company.contactPerson.toLowerCase().includes(lowerQuery) ||
            company.email.toLowerCase().includes(lowerQuery)
        );
    }

    // Filter companies by plan
    async filterByPlan(planId) {
        await delay(200);
        return this.companies.filter(company => company.subscriptionPlan === planId);
    }

    // Filter companies by status
    async filterByStatus(status) {
        await delay(200);
        return this.companies.filter(company => company.status === status);
    }

    // Get company statistics
    // Get company statistics
    async getCompanyStats(id) {
        const company = await this.getCompanyById(id);

        // Return placeholder stats until backend provides a stats endpoint for specific companies
        return {
            totalUsers: company.maxUsers || 0,
            activeUsers: 0,
            storageUsed: 0,
            totalOrders: 0,
            totalProducts: 0,
            totalCustomers: 0,
            monthlyRevenue: 0
        };
    }

    // Update company status
    async updateCompanyStatus(id, status) {
        await delay(300);
        const index = this.companies.findIndex(c => c.id === id);
        if (index === -1) {
            throw new Error(`Company with ID ${id} not found`);
        }
        this.companies[index].status = status;
        return { ...this.companies[index] };
    }

    // Extend subscription
    async extendSubscription(id, newExpiryDate) {
        await delay(400);
        const index = this.companies.findIndex(c => c.id === id);
        if (index === -1) {
            throw new Error(`Company with ID ${id} not found`);
        }
        this.companies[index].subscriptionExpiryDate = newExpiryDate;
        return { ...this.companies[index] };
    }

    // Change subscription plan
    async changeSubscriptionPlan(id, newPlanId) {
        await delay(400);
        const index = this.companies.findIndex(c => c.id === id);
        if (index === -1) {
            throw new Error(`Company with ID ${id} not found`);
        }
        const plan = SUBSCRIPTION_PLANS.find(p => p.id === newPlanId);
        if (!plan) {
            throw new Error(`Plan with ID ${newPlanId} not found`);
        }
        this.companies[index].subscriptionPlan = newPlanId;
        return { ...this.companies[index] };
    }
}

// Export singleton instance
export const companyService = new CompanyService();
